import {Color} from '@benchling/nucleus-ui';
import {UnstyledButton, createStyles} from '@mantine/core';
import React from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

const useStyles = createStyles((theme) => ({
  root: {
    paddingBottom: 0,
  },
  sectionHeader: {
    padding: '6px 16px',
    fontSize: 11,
    fontWeight: 600,
    lineHeight: '16.5px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.55px',
    // GRAY_500 (#979799) per Figma, matching the "Recent Searches" header.
    color: theme.colors.gray[5],
  },
  cardsRow: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    gap: 13,
    padding: '0 16px 16px',
  },
  cardIcon: {
    width: 36,
    height: 36,
    borderRadius: 4,
    padding: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  cardInner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    width: '100%',
    height: 36,
  },
  cardTextWrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 4,
    overflow: 'hidden',
  },
  cardTitle: {
    fontSize: 13,
    fontWeight: 600,
    lineHeight: '16px',
    color: Color.GRAY_900,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  cardSubtitle: {
    fontSize: 12,
    fontWeight: 400,
    color: Color.GRAY_900,
    lineHeight: '14px',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

interface QuickActionCardProps {
  icon: JSX.Element;
  iconBgColor: string;
  iconColor: string;
  title: string;
  subtitle: string;
  onClick: () => void;
}

function QuickActionCard({
  icon,
  iconBgColor,
  iconColor,
  title,
  subtitle,
  onClick,
}: QuickActionCardProps): JSX.Element {
  const {classes} = useStyles();
  return (
    <UnstyledButton
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        flex: 1,
        minWidth: 0,
        height: 60,
        padding: 12,
        background: Color.WHITE,
        borderRadius: 4,
        border: `1px solid ${Color.GRAY_300}`,
        transition: 'all 100ms ease',
        '&:hover': {
          backgroundColor: Color.GRAY_50,
          borderColor: Color.GRAY_400,
        },
      }}
    >
      <div className={classes.cardInner}>
        <div
          className={classes.cardIcon}
          style={{backgroundColor: iconBgColor, color: iconColor}}
          aria-hidden="true"
        >
          {icon}
        </div>
        <div className={classes.cardTextWrapper}>
          <div className={classes.cardTitle}>{title}</div>
          <div className={classes.cardSubtitle}>{subtitle}</div>
        </div>
      </div>
    </UnstyledButton>
  );
}

interface OmniboxQuickActionsProps {
  composeEnabled: boolean;
  onEntryFromTemplate: () => void;
  onEntryFromCompose: () => void;
  onOpenReviews: () => void;
}

export default function OmniboxQuickActions({
  composeEnabled,
  onEntryFromTemplate,
  onEntryFromCompose,
  onOpenReviews,
}: OmniboxQuickActionsProps): JSX.Element {
  const {classes} = useStyles();
  return (
    <div className={classes.root}>
      <div className={classes.sectionHeader}>Quick Actions</div>
      <div className={classes.cardsRow}>
        {composeEnabled && (
          <QuickActionCard
            icon={<BenchlingIcon icon="sparkle" style={{fontSize: 25}} />}
            iconBgColor={Color.GRAPE_100}
            iconColor={Color.GRAPE_350}
            title="New entry using AI"
            subtitle="Prompt-based creation"
            onClick={onEntryFromCompose}
          />
        )}
        <QuickActionCard
          icon={<BenchlingIcon icon="file-lines" style={{fontSize: 25}} />}
          iconBgColor={Color.BLUE_100}
          iconColor={Color.BLUE_350}
          title="New templated entry"
          subtitle="Create from template"
          onClick={onEntryFromTemplate}
        />
        <QuickActionCard
          icon={<BenchlingIcon icon="list-check-xmark" style={{fontSize: 25}} />}
          iconBgColor={Color.ORANGE_150}
          iconColor={Color.ORANGE_350}
          title="View pending reviews"
          subtitle="Complete open tasks"
          onClick={onOpenReviews}
        />
      </div>
    </div>
  );
}
